"use client";

/* Motion runtime is a mutable class instance (ADR-011). */
/* eslint-disable react-hooks/immutability */

import { gsap, useGSAP, registerGsap, EASE_IO, EASE_OUT, EASE_IN } from "@/lib/motion/gsap";
import { mapRect } from "@/lib/motion/geom";
import { dirMul } from "@/lib/motion/dir";
import { prefersReducedMotion } from "@/lib/motion/reduced-motion";
import {
  resolveBootSection,
  restoreBootSection,
} from "@/lib/motion/section-deep-link";
import { useMotionBag } from "@/components/site/motion/MotionContext";
import type { SiteMotion } from "@/lib/motion/site-runtime";

/** Brand X diagonal angle (path ≈ ±35° from vertical). */
const LEG_A = -35;
const LEG_B = 35;

/**
 * Measure loader_line box without cross rotation.
 * CSS rotate must be overridden with an *inline* transform — clearProps alone
 * leaves stylesheet transforms, so getBoundingClientRect returns a fat AABB.
 */
function mapFromLoaderLine(dummy: HTMLElement, line: HTMLElement) {
  gsap.set(line, { rotation: 0, transformOrigin: "50% 50%" });
  const rect = mapRect(dummy, line);
  return rect;
}

export function useLoaderMotion(motion: SiteMotion | null) {
  const { epoch, completeLoader } = useMotionBag();
  useGSAP(
    () => {
      if (!motion || !epoch) return;
      registerGsap();
      const loader = document.getElementById("loader");
      if (!loader) return;

      /* Fast Refresh can remount Loader while loaderPlayed stayed true → stuck overlay. */
      if (motion.loaderPlayed) {
        gsap.set(loader, { autoAlpha: 0 });
        gsap.set("._tempHide", { autoAlpha: 1 });
        motion.body.classList.add("site-ready");
        motion.body.classList.remove("progress");
        motion.body.classList.remove("splash-lock");
        motion.lock(false);
        completeLoader();
        restoreBootSection(motion, { unlock: true });
        return;
      }
      motion.loaderPlayed = true;

      const dummyLines = gsap.utils.toArray<HTMLElement>(".dummy_line");
      const loaderLines = gsap.utils.toArray<HTMLElement>("#loader .loader_line");
      const lineShapes = gsap.utils.toArray<HTMLElement>(".line_shape");
      const hero = gsap.utils.toArray<HTMLElement>("._hero_ele");
      const reduced = prefersReducedMotion();

      const bootTarget = resolveBootSection();
      const needsBootSeek = !!(bootTarget && bootTarget !== "hero");

      let morphReady = false;
      let seekReady = !needsBootSeek;
      let revealed = false;
      let revealTl: gsap.core.Timeline | null = null;

      const finish = () => {
        motion.lock(false);
        motion.body.classList.remove("progress");
        motion.body.classList.remove("splash-lock");
        completeLoader();
        motion.playFkVideos("play");
        motion.inview();
      };

      /**
       * Morph + quiet seek both finish under the loader, then dismiss once.
       * First paint is already the target section (no hero → snap).
       */
      const tryReveal = () => {
        if (revealed || !morphReady || !seekReady) return;
        revealed = true;

        const isMobile = window.innerWidth <= 600;
        const d = reduced ? 0.2 : isMobile ? 0.45 : 1;
        revealTl = gsap.timeline({ onComplete: finish });
        revealTl
          .to(loader, { autoAlpha: 0, duration: d, ease: EASE_OUT }, 0)
          .add(() => {
            motion.body.classList.add("site-ready");
          }, 0)
          .set("._tempHide", { autoAlpha: 1 }, 0);

        /* Hero blur-in only when landing on hero — avoid animating hidden planes. */
        if (!needsBootSeek) {
          revealTl.fromTo(
            hero,
            { blur: reduced ? 0 : 10 },
            { blur: 0, duration: reduced ? 0.01 : d, ease: EASE_OUT },
            0,
          );
        }

        revealTl
          .set(dummyLines, { autoAlpha: 0, clearProps: "transform" })
          .set(lineShapes, { autoAlpha: isMobile ? 0 : 1, scaleX: 1, scaleY: 1 })
          .set(".line_shape > i", { autoAlpha: isMobile ? 0 : 1 })
          .set("[data-meteors-host]", { clearProps: "filter" });
      };

      const markMorphReady = () => {
        morphReady = true;
        tryReveal();
      };

      const startBootSeek = () => {
        if (!needsBootSeek) return;
        restoreBootSection(motion, {
          unlock: false,
          onDone: () => {
            seekReady = true;
            tryReveal();
          },
        });
      };

      /* Morph sources missing (HMR / partial DOM) — still dismiss loader. */
      if (dummyLines.length < 2 || loaderLines.length < 2 || lineShapes.length < 2) {
        startBootSeek();
        const tl = gsap.timeline({ onComplete: markMorphReady });
        /* Hold briefly so double-rAF seek can start; tryReveal owns dismiss. */
        tl.to({}, { duration: reduced ? 0.05 : 0.2 });
        return () => {
          tl.kill();
          revealTl?.kill();
        };
      }

      gsap.set(lineShapes, { autoAlpha: 0 });
      /* Morph sources stay invisible — only the X SVG is seen during the hold. */
      gsap.set(loaderLines, { autoAlpha: 0, opacity: 0 });
      gsap.set(dummyLines, { autoAlpha: 0, opacity: 0 });

      /**
       * Desktop: X legs morph into side `.line_shape` marks.
       * Mobile (≤600): side marks stay hidden — morph toward those rails, then
       * continue outward off-screen (founder ask). The prior stuck-X bug was the
       * `#loader ._ele` fade still running past morphAt and overwriting the mark
       * hide — fade must finish by morphAt, and mark hide uses overwrite:"auto".
       */
      const isMobile = window.innerWidth <= 600;
      const morphDur = reduced ? 0.2 : isMobile ? 0.95 : 1.5;
      const exitDur = reduced ? 0.12 : isMobile ? 0.5 : 0.55;

      const morphLegs = () => {
        const legMorph = gsap.timeline();
        const from0 = mapFromLoaderLine(dummyLines[0], loaderLines[0]);
        const to0 = mapRect(dummyLines[0], lineShapes[0]);
        const from1 = mapFromLoaderLine(dummyLines[1], loaderLines[1]);
        const to1 = mapRect(dummyLines[1], lineShapes[1]);
        const shape0 = lineShapes[0].getBoundingClientRect();
        const shape1 = lineShapes[1].getBoundingClientRect();
        const mid = window.innerWidth / 2;
        const exitPad = window.innerWidth * 0.72;
        const exitX = (shape: DOMRect, baseX: number) =>
          shape.left + shape.width / 2 < mid ? baseX - exitPad : baseX + exitPad;

        legMorph
          .fromTo(
            dummyLines[0],
            {
              scaleX: from0.scaleX,
              scaleY: from0.scaleY,
              x: from0.x,
              y: from0.y,
              rotation: LEG_A,
              transformOrigin: "50% 50%",
            },
            {
              scaleX: to0.scaleX,
              scaleY: to0.scaleY,
              x: to0.x,
              y: to0.y,
              rotation: 0,
              duration: morphDur,
              ease: EASE_IO,
            },
            0,
          )
          .fromTo(
            dummyLines[1],
            {
              scaleX: from1.scaleX,
              scaleY: from1.scaleY,
              x: from1.x,
              y: from1.y,
              rotation: LEG_B,
              transformOrigin: "50% 50%",
            },
            {
              scaleX: to1.scaleX,
              scaleY: to1.scaleY,
              x: to1.x,
              y: to1.y,
              rotation: 0,
              duration: morphDur,
              ease: EASE_IO,
            },
            0,
          );

        if (isMobile) {
          legMorph
            .to(
              dummyLines[0],
              {
                x: exitX(shape0, to0.x),
                y: to0.y + window.innerHeight * 0.08,
                autoAlpha: 0,
                duration: exitDur,
                ease: EASE_IN,
              },
              morphDur,
            )
            .to(
              dummyLines[1],
              {
                x: exitX(shape1, to1.x),
                y: to1.y + window.innerHeight * 0.08,
                autoAlpha: 0,
                duration: exitDur,
                ease: EASE_IN,
              },
              morphDur,
            );
        }

        return legMorph;
      };

      const d = reduced ? 0.2 : 1;
      /*
       * Mobile LCP: start sooner / finish faster. Timeline positions are
       * relative to `delay` — do not bake holdDelay into them.
       */
      const holdDelay = reduced ? 0 : isMobile ? 0.15 : 0.5;
      const morphAt = isMobile ? 0.48 : 1;
      const morphEndAt = isMobile ? morphAt + morphDur + exitDur : 2;
      /* Fade must complete by morphAt or it fights the mark → 0 handoff. */
      const fadeDur = isMobile ? Math.min(d, morphAt) : d;
      const tl = gsap.timeline({ delay: holdDelay });
      tl.set(".loader_logo", { autoAlpha: 1 }, 0)
        .fromTo(
          "#loader ._ele",
          { autoAlpha: 0 },
          {
            autoAlpha: 1,
            duration: fadeDur,
            ease: EASE_OUT,
            stagger: 0.02,
          },
          0,
        )
        .call(startBootSeek, undefined, morphAt)
        .to(
          ".loader_suffix",
          {
            autoAlpha: 0,
            duration: reduced ? 0.1 : isMobile ? 0.22 : 0.35,
            ease: EASE_OUT,
          },
          morphAt,
        )
        /* overwrite kills any in-flight ._ele fade still targeting the mark */
        .set(".loader_mark", { autoAlpha: 0, overwrite: "auto" }, morphAt)
        .set(dummyLines, { autoAlpha: 1 }, morphAt)
        .add(morphLegs(), morphAt)
        .call(markMorphReady, undefined, morphEndAt);

      return () => {
        tl.kill();
        revealTl?.kill();
      };
    },
    { dependencies: [motion, epoch, completeLoader] },
  );
}

export function useHeaderMotion(motion: SiteMotion | null) {
  useGSAP(
    () => {
      if (!motion) return;
      registerGsap();
      const menu = document.querySelector<HTMLElement>(".header_menu");
      const expandSet = document.querySelector<HTMLElement>(".header_expand_set");
      const header = document.querySelector<HTMLElement>("header");
      if (!menu || !expandSet || !header) return;

      const d = dirMul();

      /** Compact chrome: ≤600 width OR coarse-pointer short-height phone landscape. */
      const wantsCompactChrome = () => {
        if (window.innerWidth <= 600) return true;
        const coarse =
          window.matchMedia("(pointer: coarse)").matches || motion.isTouch;
        return coarse && window.innerHeight <= 500 && window.innerWidth > 600;
      };

      const snapCompact = () => {
        motion.headerTl?.pause(0);
        motion.headerTl?.kill();
        motion.headerTl = null;
        /* Clear stale timeline width/transforms so the chip is expand-set only. */
        gsap.set("._update", {
          width: expandSet.offsetWidth,
          clearProps: "transform",
          overwrite: "auto",
        });
        gsap.set(".header_menu", {
          autoAlpha: 0,
          width: 0,
          overwrite: "auto",
        });
        gsap.set("._update ._ele", {
          autoAlpha: 0,
          x: -0.02 * window.innerWidth * d,
          overwrite: "auto",
        });
        gsap.set(expandSet, {
          autoAlpha: 1,
          x: 0,
          overwrite: "auto",
        });
      };

      const buildDesktopTl = () => {
        motion.headerTl?.kill();
        motion.headerTl = gsap.timeline({ paused: true });
        motion.headerTl
          .fromTo(
            "._update",
            { width: () => menu.offsetWidth },
            { width: () => expandSet.offsetWidth, duration: 1, ease: EASE_IO },
            0,
          )
          .fromTo(
            "._update ._ele",
            { x: 0, autoAlpha: 1 },
            {
              x: -0.02 * window.innerWidth * d,
              autoAlpha: 0,
              stagger: 0.02,
              duration: 0.5,
              ease: EASE_IN,
            },
            0,
          )
          .fromTo(
            expandSet,
            { x: 0.02 * window.innerWidth * d, autoAlpha: 0 },
            { x: 0, autoAlpha: 1, duration: 0.5, ease: EASE_OUT },
            0.5,
          );
      };

      const apply = () => {
        if (wantsCompactChrome()) {
          /* Snap — never play the desktop width morph on mobile. */
          motion.headerTl?.kill();
          motion.headerTl = null;
          snapCompact();
          return;
        }
        buildDesktopTl();
        motion.headerTl?.reverse();
      };

      apply();

      /* Width reload (ADR-018) handles large changes; only re-apply on compact gate flip. */
      let wasCompact = wantsCompactChrome();
      const onResize = () => {
        const compact = wantsCompactChrome();
        if (compact === wasCompact) {
          if (compact) snapCompact();
          return;
        }
        wasCompact = compact;
        apply();
      };
      window.addEventListener("resize", onResize);

      const onHeaderEnter = () => {
        if (wantsCompactChrome()) return;
        motion.headerTl?.reverse();
      };
      const onHeaderLeave = () => {
        if (wantsCompactChrome()) return;
        motion.headerTl?.play();
      };

      /* Touch: keep the collapsed chip. Do not reverse on tap. */
      if (!motion.isTouch) {
        header.addEventListener("mouseenter", onHeaderEnter);
        header.addEventListener("mouseleave", onHeaderLeave);
      }

      return () => {
        window.removeEventListener("resize", onResize);
        header.removeEventListener("mouseenter", onHeaderEnter);
        header.removeEventListener("mouseleave", onHeaderLeave);
        motion.headerTl?.kill();
      };
    },
    { dependencies: [motion] },
  );
}
